import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Rate, Tabs, Skeleton, Col, Row } from 'antd';
import { useTranslation } from 'react-i18next';

import { fetchProduct, fetchReview } from 'store/slices/productSlice';
import HeadingTitle from 'components/HeadingTitle';
import ProductItemMain from './ProductItemMain';
import Reviews from './Reviews';
import fetcher, { loadingStatus } from 'helpers/fetcher';
import useSmartNavigate from 'hooks/useSmartNavigate';
import { menuFlatter } from 'helpers';
import { formatWeightWithUnit } from 'helpers/formatter';
import { getStock } from 'helpers/product';
import ProductPriceRow from 'components/product/ProductPriceRow';
import WishlistButton from 'components/cart/WishlistButton';
import AddToCartButton from 'components/cart/AddToCartButton';
import Icon from 'components/Icon';

const ProductDescription = ({ description, onExpand, onCollapse }) => {
	const { t } = useTranslation();
	const [isExpanded, setIsExpanded] = useState(false);

	const hasDescription = description && description.trim().length > 0;

	const handleExpand = () => {
		setIsExpanded(true);
		if (onExpand) {
			onExpand();
		}
	};

	const handleCollapse = () => {
		setIsExpanded(false);
		if (onCollapse) {
			onCollapse();
		}
	};

	return (
		<div className='product-description-wrapper'>
			<div
				className={`rn-description product-description ${
					!isExpanded ? 'product-description-collapsed' : ''
				}`}
				dangerouslySetInnerHTML={{
					__html: description || '',
				}}
			/>
			{hasDescription && !isExpanded && (
				<button className='product-read-more-link' onClick={handleExpand}>
					{t('catalog.readMore')}
				</button>
			)}
			{hasDescription && isExpanded && (
				<button className='product-read-more-link' onClick={handleCollapse}>
					{t('catalog.hideText')}
				</button>
			)}
		</div>
	);
};

const ProductInfoTabs = ({
	data,
	review,
	onDescriptionExpand,
	onDescriptionCollapse,
	t,
}) => [
	{
		key: '1',
		label: t('product.aboutProduct'),
		children: (
			<ProductDescription
				description={data?.onec_description}
				onExpand={onDescriptionExpand}
				onCollapse={onDescriptionCollapse}
			/>
		),
	},
	{
		key: '2',
		label: t('product.reviews'),
		children: (
			<div className='product-reviews-placeholder'>
				<p>{t('product.noReviews')}</p>
			</div>
		),
	},
];

const Product = () => {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { product, review } = useSelector(state => state.product);
	const { data: menuData } = useSelector(state => state.menu.categoriesList);
	const { '*': path } = useParams();
	const { navigate: smartNavigate } = useSmartNavigate();
	const [product_id] = path.split('/');
	const [tabsHeight, setTabsHeight] = useState(null);
	const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
	const [activeTabKey, setActiveTabKey] = useState('1');
	const [recommendedProducts, setRecommendedProducts] = useState([]);
	const [recommendedLoading, setRecommendedLoading] = useState(true);
	const imageRef = useRef(null);
	const detailsCardRef = useRef(null);
	const tabsSectionRef = useRef(null);

	const handleDescriptionExpand = () => {
		setIsDescriptionExpanded(true);
	};

	const handleDescriptionCollapse = () => {
		setIsDescriptionExpanded(false);
		// При сворачивании возвращаем ограничение высоты
		if (imageRef.current && detailsCardRef.current) {
			const imageHeight = imageRef.current.offsetHeight;
			const detailsCardHeight = detailsCardRef.current.offsetHeight;
			const marginBetween = 24;
			const calculatedHeight = imageHeight - detailsCardHeight - marginBetween;

			if (calculatedHeight > 0) {
				setTabsHeight(calculatedHeight);
			} else {
				setTabsHeight(null);
			}
		}
	};

	useEffect(() => {
		dispatch(fetchProduct(product_id));
		dispatch(fetchReview(product_id));
	}, [dispatch, product_id]);

	// Рекомендуемые: related из API, при нехватке — один запрос product/popular (без обхода категорий)
	useEffect(() => {
		const loadRecommendedProducts = async () => {
			if (product.status !== loadingStatus.SUCCEEDED || !product.data) {
				return;
			}

			setRecommendedLoading(true);

			const apiProducts = product.data.products || [];
			const inStockProducts = apiProducts.filter(item => {
				if (!item || !item.product_id || item.product_id === product_id)
					return false;
				const stock = getStock(item);
				return stock.stock > 0;
			});

			if (inStockProducts.length >= 4) {
				setRecommendedProducts(inStockProducts.slice(0, 4));
				setRecommendedLoading(false);
				return;
			}

			const result = [...inStockProducts];

			try {
				const url = `/index.php?route=product/popular&limit=8&exclude_product_id=${product_id}`;
				const response = await fetcher(url);
				if (response && response.ok) {
					const data = await response.json();
					const list = data.products || [];
					for (const p of list) {
						if (result.length >= 4) break;
						if (!p?.product_id || p.product_id === product_id) continue;
						if (result.some(x => x.product_id === p.product_id)) continue;
						const stock = getStock(p);
						if (stock.stock > 0) result.push(p);
					}
				}
			} catch (e) {
				// ignore
			}

			setRecommendedProducts(result.slice(0, 4));
			setRecommendedLoading(false);
		};

		loadRecommendedProducts();
	}, [product.status, product.data, product_id]);

	// Вычисляем высоту блока с вкладками на основе высоты картинки
	useEffect(() => {
		const calculateTabsHeight = () => {
			if (
				imageRef.current &&
				detailsCardRef.current &&
				tabsSectionRef.current
			) {
				const imageHeight = imageRef.current.offsetHeight;
				const detailsCardHeight = detailsCardRef.current.offsetHeight;
				const marginBetween = 24; // margin-top между блоками

				// Высота блока с вкладками = высота картинки - высота блока с ценой - отступ между ними
				const calculatedHeight =
					imageHeight - detailsCardHeight - marginBetween;

				if (calculatedHeight > 0) {
					setTabsHeight(calculatedHeight);
				} else {
					setTabsHeight(null);
				}
			}
		};

		// Вычисляем после загрузки изображения
		if (product.status === loadingStatus.SUCCEEDED && product.data) {
			const timer = setTimeout(() => {
				calculateTabsHeight();
			}, 100);

			window.addEventListener('resize', calculateTabsHeight);

			return () => {
				clearTimeout(timer);
				window.removeEventListener('resize', calculateTabsHeight);
			};
		}
	}, [product.status, product.data]);

	// Находим категорию товара для breadcrumbs
	const productCategory = useMemo(() => {
		if (!product.data?.category_id || !menuData?.categories)
			return { parent: null, current: null };

		const flat = menuFlatter(menuData.categories);
		const currentCategory = flat.find(
			cat =>
				cat.category_id === product.data.category_id ||
				cat.key === product.data.category_id
		);

		if (!currentCategory) return { parent: null, current: null };

		// Ищем родительскую категорию
		let parentCategory = null;
		const findParent = (categories, targetId, parent = null) => {
			for (const cat of categories || []) {
				if (cat.category_id === targetId || cat.key === targetId) {
					return parent;
				}
				if (cat.children) {
					const found = findParent(cat.children, targetId, cat);
					if (found !== null) return found;
				}
			}
			return null;
		};

		parentCategory = findParent(menuData.categories, product.data.category_id);

		return {
			parent: parentCategory,
			current: currentCategory,
		};
	}, [product.data?.category_id, menuData]);

	return (
		<div className='region product-section'>
			{/* Breadcrumb */}
			{product.status === loadingStatus.SUCCEEDED && product.data ? (
				<div className='contact-breadcrumb' style={{ marginTop: '64px' }}>
					<a
						href='/'
						onClick={e => {
							e.preventDefault();
							navigate('/');
						}}
						className='breadcrumb-link'
				>
					{t('common.home')}
				</a>
				<span
					className={`breadcrumb-separator ${
						!productCategory.parent ? 'breadcrumb-separator-active' : ''
					}`}
				></span>
					{productCategory.parent && (
						<>
							<a
								href={`/catalog/${productCategory.parent.path}`}
								onClick={e => {
									e.preventDefault();
									smartNavigate(`/catalog/${productCategory.parent.path}`);
								}}
								className='breadcrumb-link'
							>
								{productCategory.parent.name ||
									productCategory.parent.label ||
									productCategory.parent.title}
							</a>
							<span className='breadcrumb-separator breadcrumb-separator-active'></span>
						</>
					)}
					{productCategory.current && (
						<>
							<a
								href={`/catalog/${productCategory.current.path}`}
								onClick={e => {
									e.preventDefault();
									smartNavigate(`/catalog/${productCategory.current.path}`);
								}}
								className='breadcrumb-link'
							>
								{productCategory.current.name ||
									productCategory.current.label ||
									productCategory.current.title}
							</a>
							<span className='breadcrumb-separator breadcrumb-separator-active'></span>
						</>
					)}
				<span className='breadcrumb-current'>
					{product.data.heading_title || product.data.name || t('common.productNotFound')}
				</span>
				</div>
			) : (
				<div className='contact-breadcrumb' style={{ marginTop: '64px' }}>
					<Skeleton active paragraph={{ rows: 1 }} />
				</div>
			)}

			<Row gutter={[48, 48]} className='mb-30' style={{ marginTop: '48px' }}>
				<Col xs={24} md={12}>
					<div className='product-image-wrapper'>
						{product.status === loadingStatus.SUCCEEDED &&
						product.data &&
						product.data.popup ? (
							<img
								ref={imageRef}
								src={product.data.popup}
								alt={product.data.heading_title || product.data.name}
								className='product-main-image'
								onLoad={() => {
									// Пересчитываем высоту после загрузки изображения
									setTimeout(() => {
										if (
											imageRef.current &&
											detailsCardRef.current &&
											tabsSectionRef.current
										) {
											const imageHeight = imageRef.current.offsetHeight;
											const detailsCardHeight =
												detailsCardRef.current.offsetHeight;
											const marginBetween = 24;
											const calculatedHeight =
												imageHeight - detailsCardHeight - marginBetween;
											if (calculatedHeight > 0) {
												setTabsHeight(calculatedHeight);
											} else {
												setTabsHeight(null);
											}
										}
									}, 100);
								}}
							/>
						) : product.status === loadingStatus.LOADING ? (
							<Skeleton.Image
								active
								style={{ width: '100%', height: '500px' }}
							/>
						) : null}
					</div>
				</Col>
				<Col xs={24} md={12}>
					{product.status === loadingStatus.SUCCEEDED &&
					product.data &&
					product.data.heading_title ? (
						<>
							<div ref={detailsCardRef}>
								<ProductItemMain product_id={product_id} data={product.data} />
							</div>
							<div
								ref={tabsSectionRef}
								className={`product-tabs-section ${
									isDescriptionExpanded ? 'expanded' : ''
								}`}
								style={{
									marginTop: '24px',
									minHeight:
										isDescriptionExpanded || !tabsHeight
											? 'auto'
											: `${tabsHeight}px`,
								}}
							>
								<Tabs
									activeKey={activeTabKey}
									onChange={setActiveTabKey}
								items={ProductInfoTabs({
									data: product.data,
									review,
									onDescriptionExpand: handleDescriptionExpand,
									onDescriptionCollapse: handleDescriptionCollapse,
									t,
								})}
								/>
							</div>
						</>
					) : product.status === loadingStatus.LOADING ? (
						<Skeleton active paragraph={{ rows: 8 }} />
					) : (
						<div>{t('common.productNotFound')}</div>
					)}
				</Col>
			</Row>

			{product.status === loadingStatus.SUCCEEDED &&
			product.data &&
			product.data.heading_title ? (
				<>
					{(recommendedProducts.length > 0 || recommendedLoading) && (
						<div className='recommended-products-section'>
							<div
								className='recommended-products-header'
								style={{
									display: 'flex',
									justifyContent: 'space-between',
									alignItems: 'center',
									marginBottom: '24px',
								}}
							>
							<HeadingTitle
								title={product.data.text_related || t('product.recommendedProducts')}
							/>
								{recommendedProducts.length > 0 && (
									<a
										href='/catalog'
										className='popular-products-link catalog-link-all'
										onClick={e => {
											e.preventDefault();
											navigate('/catalog');
										}}
									>
										<span className='catalog-link-text'>{t('home.toCatalog')}</span>
										<Icon
											name='icon-arrow-right-gray'
											className='catalog-link-arrow'
										/>
									</a>
								)}
							</div>
							<Row gutter={[24, 24]} className='popular-products-list'>
								{recommendedLoading
									? Array(4)
											.fill({})
											.map((_, index) => (
												<Col xs={12} sm={12} md={8} lg={6} key={index}>
													<Skeleton active avatar paragraph={{ rows: 4 }} />
												</Col>
											))
									: recommendedProducts.map((item, index) => (
											<Col
												xs={12}
												sm={12}
												md={8}
												lg={6}
												key={item.product_id || index}
											>
												<div
													className='popular-product-card'
													data-click-key={
														item.product_id ? `product:${item.product_id}` : undefined
													}
													onClick={() =>
														navigate(`/product/${item.product_id}/${item.slug}`)
													}
													style={{ cursor: 'pointer' }}
												>
													<div className='popular-product-image-wrapper'>
														<img
															src={item.thumb}
															alt={item.name}
															className='popular-product-image'
														/>
														{item.rating && (
															<div className='popular-product-rating'>
																<Icon
																	name='icon-star'
																	className='popular-product-rating-icon'
															ariaLabel={t('home.rating')}
															ariaHidden={false}
																/>
																<span className='popular-product-rating-value'>
																	{item.rating}
																</span>
															</div>
														)}
													</div>
													<div
														className='popular-product-favorite'
														onClick={e => e.stopPropagation()}
													>
														<WishlistButton
															product_id={item.product_id}
															active={item.in_wishlist}
														/>
													</div>
													<h3 className='popular-product-title'>
														{item.name}, {formatWeightWithUnit(item.weight)}
													</h3>
													<ProductPriceRow item={item} />
													<div onClick={e => e.stopPropagation()}>
														<AddToCartButton item={item} />
													</div>
												<div className='popular-product-stock'>
													{(() => {
														const stock = getStock(item);
														return stock.stock === 999
															? t('stock.inStockMany')
															: t('stock.inStockCount', { count: stock.stock });
													})()}
													</div>
												</div>
											</Col>
									  ))}
							</Row>
						</div>
					)}
				</>
			) : product.status === loadingStatus.LOADING ? (
				<>
					<Skeleton active paragraph={{ rows: 4 }} />
					<Skeleton active paragraph={{ rows: 4 }} />
				</>
			) : null}
		<div className='region quality-section' style={{ marginTop: '60px' }}>
			<h2 className='quality-title'>{t('quality.title')}</h2>
			<div className='quality-cards'>
				<div className='quality-card quality-card-gray'>
					<img
						src={`${process.env.PUBLIC_URL || ''}/images/icon-halal.webp`}
						alt=''
						className='quality-card-icon'
					/>
					<h4 className='quality-card-text'>{t('quality.noPork')}</h4>
				</div>
				<div className='quality-card quality-card-gray'>
					<img
						src={`${process.env.PUBLIC_URL || ''}/images/icon-eggs.webp`}
						alt=''
						className='quality-card-icon'
					/>
					<h4 className='quality-card-text'>{t('quality.naturalEggs')}</h4>
				</div>
				<div className='quality-card quality-card-gray'>
					<img
						src={`${process.env.PUBLIC_URL || ''}/images/icon-cow.webp`}
						alt=''
						className='quality-card-icon'
					/>
					<h4 className='quality-card-text'>{t('quality.ownSlaughter')}</h4>
				</div>
				<div className='quality-card quality-card-gray'>
					<img
						src={`${process.env.PUBLIC_URL || ''}/images/icon-nature.webp`}
						alt=''
						className='quality-card-icon'
					/>
					<h4 className='quality-card-text'>{t('quality.noPreservatives')}</h4>
				</div>
				<div
					className='quality-card quality-card-blue'
					style={{
						backgroundImage: `url(${
							process.env.PUBLIC_URL || ''
						}/images/bg-quality-card.webp)`,
						backgroundSize: 'cover',
						backgroundPosition: 'center',
						backgroundRepeat: 'no-repeat',
					}}
				>
					<Icon
						name='icon-dna'
						className='quality-card-icon'
					/>
					<h4 className='quality-card-text quality-card-text-white'>
						{t('quality.noGMO')}
					</h4>
				</div>
			</div>
		</div>
		</div>
	);
};

export default Product;
